import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { usePermissions } from "@/app/providers/AuthProvider";
import { notificationListQuery } from "@/features/notifications/api/notifications.queries";
import {
  CARD_TOUCH_LINK,
  notificationTarget,
  relativeTime,
} from "@/features/notifications/lib/notification-labels";
import { SkLine } from "@altek/ui/components/feedback/Skeleton";
import { formatDateTime } from "@altek/ui/lib/format/date";

/**
 * Üst çubuktaki zilin altına açılan liste.
 *
 * NEDEN VAR. Zil bir sayaç gösteriyordu ve basınca hiçbir şey olmuyordu — sayı artıyor, kişi
 * basıyor, ekran değişmiyor. Bir rozetin tek işi dikkat çekmek; çektiği dikkatin gidecek bir
 * yeri yoksa rozet yalan söylüyor demektir.
 *
 * BEŞ SATIR, HEPSİ DEĞİL. Bu bir ÖNİZLEME: en son beş bildirim ve altında tam listeye giden
 * bağlantı. Otuz satırı buraya sığdırmak, pencereyi kaydırılır bir ikinci sayfaya çevirir ve
 * «tümünü gör»ü anlamsız kılardı.
 *
 * NE ZAMAN VE NEREYE (İK müdürü turu, 7 Ekim 2026). Satır ne zaman geldiğini söylemiyordu — aynı
 * başlıklı yedi doğum günü bildirimi yedi farklı günün bildirimiydi ve ayırt edilemiyordu. Her
 * satırın altında göreli zaman («3 gün önce», üzerine gelince tam tarih). Ve satır kaydın kendisine
 * gidiyor (`notificationTarget`): sohbet mesajı konuşmaya, karar verilen izin kişinin kendi
 * listesine; bilinmeyen bir tür bildirim listesine.
 */
export function NotificationsPanel({ onNavigate }: { onNavigate?: (() => void) | undefined }) {
  const permissions = usePermissions();
  // Kit zili her hesapta çiziyor; anahtarı olmayan hesapta (taşımacının şoförü, 1 Kasım 2026) liste
  // istenmiyor ve kutu «Henüz bildiriminiz yok» diyor — 403 yerine.
  const canReadNotifications = permissions.can("notifications.notification.read");
  const list = useQuery({
    ...notificationListQuery({ page: 1, limit: 5 }),
    enabled: canReadNotifications,
  });
  const rows = list.data?.rows ?? [];

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-2">
        <span className="text-sm font-semibold text-fg">Bildirimler</span>
        {rows.length > 0 ? (
          <Link
            to="/notifications"
            onClick={onNavigate}
            // Telefonda 44 piksel (9 Ekim 2026) — Panelim kutularıyla aynı kural (`CARD_TOUCH_LINK`).
            className={`rounded-sm text-xs text-fg-muted underline underline-offset-2 ${CARD_TOUCH_LINK}`}
          >
            Tümünü gör
          </Link>
        ) : null}
      </div>

      {list.isLoading ? (
        /* İskelet, dönen çark değil: gelecek şeyin ŞEKLİNİ gösteriyor — deponun kuralı. */
        <div className="flex flex-col gap-2 p-3">
          <SkLine w="75%" />
          <SkLine w="100%" />
          <SkLine w="60%" />
        </div>
      ) : list.isError ? (
        /* Düşen istek «Henüz bildiriminiz yok» demiyor (kullanılabilirlik turu 2, 9 Ekim 2026). */
        <p role="status" className="px-3 py-6 text-center text-sm text-fg-muted">
          Bildirimler yüklenemedi. Biraz sonra yeniden açın.
        </p>
      ) : rows.length === 0 ? (
        <p className="px-3 py-6 text-center text-sm text-fg-muted">Henüz bildiriminiz yok.</p>
      ) : (
        <ul className="flex flex-col">
          {rows.map((row) => (
            <li key={row.id} className="border-b border-border last:border-b-0">
              {/* pz-gorev 6 Ekim 2026: adres taşıyan bildirim kaydın kendisine götürüyor. */}
              <Link
                to={notificationTarget(row) ?? "/notifications"}
                onClick={onNavigate}
                className="flex flex-col gap-0.5 px-3 py-2.5"
              >
                <span className="flex items-center gap-2">
                  {/*
                    Okunmamışın işareti NOKTA: kalın yazı, uzun bir başlıkta fark edilmiyor. Nokta
                    görsel; ekran okuyucu aynı bilgiyi bir kelimeden alıyor (7 Ekim 2026). `bg-brand`
                    (3 Kasım 2026): `bg-accent` diye bir simge yok, nokta hiç çizilmiyordu.
                  */}
                  {row.isRead ? null : (
                    <>
                      <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-brand" />
                      <span className="sr-only">Okunmamış: </span>
                    </>
                  )}
                  <span className="truncate text-sm font-medium text-fg">{row.title}</span>
                </span>
                {row.body ? (
                  <span className="line-clamp-2 text-xs text-fg-muted">{row.body}</span>
                ) : null}
                <time
                  dateTime={row.createdAt}
                  title={formatDateTime(row.createdAt)}
                  className="text-2xs text-fg-muted"
                >
                  {relativeTime(row.createdAt)}
                </time>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
